import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmergeMark } from '@/components/EmergeMark';
import { resolveLandingPathWithSet } from '@/lib/use-landing-page';
import { Avatar, Badge, Button } from '@/components/ui';
import { Tooltip } from '@/components/ui/Tooltip';
import { EmailLink } from '@/components/ui/TextLink';
import { cx } from '@/lib/cx';
import { isSuperAdmin, ROLE_LABELS } from '@/lib/roles';
import { useAuth } from '@/lib/use-auth';
import { useBodyScrollLock, useSidebarCollapsed } from '@/lib/use-sidebar';
import { useTheme, type ThemeMode } from '@/lib/use-theme';
import { BuildSwitcher } from './BuildSwitcher';
import { GlobalSearch } from './GlobalSearch';
import { GuideDrawer } from './guide/GuideDrawer';
import { PreviewBanner } from './PreviewBanner';
import { Sidebar } from './Sidebar';
import { TaskReminders } from './TaskReminders';
import { EnquiryAlerts } from '@/components/EnquiryAlerts';
import { EnquirySummaryProvider } from '@/lib/use-enquiry-summary';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
// Roving focus covers the theme radios as well as the plain items.
const MENU_ITEMS = '[role="menuitem"], [role="menuitemradio"]';

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function MenuIcon() {
  return (
    <svg {...iconProps} className="h-5 w-5">
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg {...iconProps} className="h-4 w-4 shrink-0">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg {...iconProps} className="h-4 w-4 shrink-0">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

function NotificationsMenuIcon() {
  return (
    <svg {...iconProps} className="h-4 w-4 shrink-0">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg {...iconProps} className="h-4 w-4 shrink-0">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg {...iconProps} className="h-4 w-4 shrink-0">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg {...iconProps} className="h-4 w-4 shrink-0">
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...iconProps} className="h-3.5 w-3.5 shrink-0 text-fg">
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

function SupportIcon() {
  return (
    <svg {...iconProps} className="hidden h-4 w-4 shrink-0 sm:block">
      <path d="M12 3 4.5 6v5.5c0 4.3 3.1 7.9 7.5 9 4.4-1.1 7.5-4.7 7.5-9V6L12 3Z" />
      <circle cx="12" cy="11" r="2.2" />
    </svg>
  );
}

const THEME_OPTIONS: { value: ThemeMode; label: string; icon: ReactNode }[] = [
  { value: 'system', label: 'System', icon: <SystemIcon /> },
  { value: 'light', label: 'Light', icon: <SunIcon /> },
  { value: 'dark', label: 'Dark', icon: <MoonIcon /> },
];

const MINUTE_MS = 60_000;

/**
 * `floor`, not `ceil`, and the two are not interchangeable: floor reads "at
 * least this long", which is the direction that cannot cost somebody work.
 * Rounding up would announce 30 minutes with 29 and a half left.
 */
function remainingLabel(ms: number): string {
  if (Number.isNaN(ms)) return 'Time left unknown';
  if (ms <= 0) return 'Expired';
  const minutes = Math.floor(ms / MINUTE_MS);
  return minutes === 0 ? 'Under a minute left' : `${minutes} min left`;
}

/**
 * The banner half of the support-session signal — whose account this is, how
 * long is left, and the way out. The window frame is the other half and is
 * rendered by `AppShell` itself.
 *
 * This copy is not helper text: it names the one state a reader cannot get from
 * anything else on the screen, which is the whole failure being designed
 * against — acting inside somebody else's account and reading it as your own.
 * So it says who, how long, and how to leave, and nothing else. No explanation
 * of what a support session is, and neither `startedBy` nor `reason` — those
 * are context for the audit trail, not for the person who is already in one.
 *
 * The countdown state lives HERE rather than in `AppShell` on purpose: a tick
 * re-renders this leaf and nothing else. Held one level up it would re-render
 * the shell, and with it the sticky bar, the sidebar and the search box, once a
 * minute for as long as the tab is open.
 *
 * Mount it with `key={session.expiresAt}` so a second session starts its own
 * countdown rather than inheriting the last one's. The key is a session
 * IDENTITY only — nothing here derives the remaining time from `expiresAt`, for
 * the reason set out on `remainingMs` below.
 */
function SupportSessionBanner({
  session,
  remainingMs,
  onEnd,
}: {
  /*
   * Deliberately the narrow shape this banner actually reads, not the whole
   * `supportSession` contract — that is `AuthContextValue`'s to declare, and a
   * second copy of it here would be a second thing to keep in step.
   */
  session: { actingAs: { name: string; email: string } };
  /**
   * `useAuth().supportSessionRemainingMs` — **the single source for how long is
   * left**, passed in rather than read here.
   *
   * ⚠️ **This used to reach into `localStorage` for the deadline**, because it
   * lives on the stored record and not on the `SupportSessionInfo` the context
   * exposes. That was two sources for one session — the context saying
   * *whether*, storage saying *until when* — and nothing would have failed if
   * they disagreed: the banner would simply have counted down against a session
   * the app had already left, or vice versa.
   *
   * ⚠️ It is still SKEW-FREE, which is the property that must survive any
   * change here: the provider derives its deadline from the server's remaining
   * DURATION (`expiresIn`) and never by parsing `expiresAt`. `expiresAt` is
   * documented in `session.ts` as "displayed; never the thing compared
   * against", and a countdown is a comparison however it looks. **Do not
   * reintroduce one here.**
   *
   * Returns `NaN` when nothing can be measured, never `0`: the banner is
   * mounted, so a session is live, and rendering "Expired" over one that is not
   * is a confident wrong answer where "unknown" is the true one.
   */
  remainingMs: () => number;
  onEnd: () => void;
}) {
  const [left, setLeft] = useState(remainingMs);

  /*
   * One `setTimeout`, rescheduled to land just past the next whole-minute
   * boundary — roughly 30 wake-ups across a 30-minute session rather than the
   * 1,800 a per-second interval would cost, and no work at all once it has
   * expired.
   *
   * Each wake-up recomputes against the deadline rather than subtracting a
   * fixed step, so the figure cannot drift: a throttled background tab, or a
   * laptop that slept for twenty minutes, corrects itself on the next tick
   * instead of counting down from where it left off.
   *
   * ⚠️ Reaching zero renders "Expired" and does NOTHING else. Ending the
   * session is `session.ts`'s call and `AuthProvider`'s to act on — it holds
   * the deadline, the token and the `onSupportSessionEnded` listeners. A
   * countdown that also pulled the trigger would be a second authority on when
   * a session is over, and the two would eventually disagree.
   */
  useEffect(() => {
    if (!Number.isFinite(left) || left <= 0) return;
    const timer = window.setTimeout(() => setLeft(remainingMs), (left % MINUTE_MS) + 250);
    return () => window.clearTimeout(timer);
  }, [left, remainingMs]);

  // A hidden tab's timers are throttled, so returning to one is the moment the
  // displayed figure is most likely to be stale. Recompute on the way back in.
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'visible') setLeft(remainingMs);
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [remainingMs]);

  // `name` can be an empty string on a staff record, and an empty string in a
  // value slot reads as a missing record rather than an unnamed one.
  const who = session.actingAs.name.trim() || session.actingAs.email.trim() || 'another user';

  return (
    <div
      role="region"
      aria-label="Support session"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-warning-border bg-warning-soft px-3 py-1.5 text-xs text-warning sm:gap-x-3 sm:px-6"
    >
      <SupportIcon />
      <p className="m-0 flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5">
        <span className="shrink-0">Acting as</span>
        <span className="truncate font-semibold">{who}</span>
        {session.actingAs.email.trim() !== '' && (
          <EmailLink address={session.actingAs.email} className="hidden md:inline" />
        )}
      </p>
      <span className="shrink-0 font-medium tabular-nums">{remainingLabel(left)}</span>
      <Button size="sm" onClick={onEnd} className="shrink-0">
        Switch back
      </Button>
    </div>
  );
}

interface ShellUser {
  name: string;
  email: string;
  role: string;
}

/**
 * Account menu. A real ARIA menu: `aria-haspopup`/`aria-expanded` on the
 * trigger, arrow-key roving inside, Escape and click-outside to dismiss, and
 * focus handed back to the trigger whenever the keyboard closed it.
 */
function UserMenu({ user, onSignOut }: { user: ShellUser; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const { mode, selectMode } = useTheme();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const focusTrigger = useCallback(() => {
    triggerRef.current?.focus();
  }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
    focusTrigger();
  }, [focusTrigger]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(MENU_ITEMS)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Tabbing out of the widget closes it without stealing the keypress, so focus
  // continues to whatever follows the trigger.
  function onRootBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? [],
    );
    if (items.length === 0) return;
    event.preventDefault();

    const current = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowDown'
            ? (current + 1) % items.length
            : (current - 1 + items.length) % items.length;
    items[next]?.focus();
  }

  return (
    <div ref={rootRef} className="relative" onBlur={onRootBlur}>
      <Tooltip label="Account menu">
        <Button
          ref={triggerRef}
          variant="ghost"
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={onTriggerKeyDown}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          className="gap-2 px-1.5 sm:px-2"
        >
          <Avatar name={user.name} size="sm" />
          <span className="sr-only sm:hidden">{user.name}</span>
          <span className="hidden min-w-0 flex-col items-start text-left sm:flex">
            <span className="max-w-[9rem] truncate text-xs leading-4 font-medium text-fg">
              {user.name}
            </span>
            <span className="max-w-[9rem] truncate text-2xs leading-4 text-fg-muted capitalize">
              {user.role}
            </span>
          </span>
          {/* Hidden below `sm` so the top bar fits 320px; the avatar is the control. */}
          <span className="hidden sm:inline-flex">
            <ChevronIcon />
          </span>
        </Button>
      </Tooltip>

      {/*
       * The menu's `z-30` is LOCAL — it orders the menu against its siblings
       * inside the top bar, which is its own stacking context, and says nothing
       * about whether the menu clears the page. If a table or a pinned column
       * ever paints over this menu again, the number to move is the BAR's (see
       * the ladder in `src/index.css`), not this one: raising this to 9999
       * would change nothing on screen.
       */}
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          onKeyDown={onMenuKeyDown}
          className="absolute top-full right-0 z-30 mt-1 w-60 overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md"
        >
          <div className="border-b border-border px-3 py-2.5">
            <p className="truncate text-sm font-medium text-fg">{user.name}</p>
            <p className="truncate text-xs">
              <EmailLink address={user.email} tone="muted" />
            </p>
            <span className="mt-1.5 inline-flex">
              <Badge tone="neutral" className="capitalize">
                {user.role}
              </Badge>
            </span>
          </div>

          <div role="menu" aria-label="Account" className="p-1">
            <div role="group" aria-label="Appearance" className="mb-1 border-b border-border pb-1">
              <p className="px-2 pt-1 pb-1 text-xs font-medium text-fg-muted">
                Appearance
              </p>
              {THEME_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  role="menuitemradio"
                  aria-checked={mode === option.value}
                  variant="ghost"
                  size="sm"
                  onClick={() => selectMode(option.value)}
                  leadingIcon={option.icon}
                  className="w-full justify-start"
                >
                  <span className="flex-1 text-left">{option.label}</span>
                  {mode === option.value && <CheckIcon />}
                </Button>
              ))}
            </div>

            <Button
              role="menuitem"
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
              leadingIcon={<NotificationsMenuIcon />}
              className="mb-1 w-full justify-start"
            >
              Notifications
            </Button>

            <Button
              role="menuitem"
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              leadingIcon={<SignOutIcon />}
              className="w-full justify-start"
            >
              Sign out
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The signed-in shell: a collapsible sidebar (an off-canvas drawer below `lg`),
 * a sticky top bar with search and the account menu, and the page's `<main>`.
 * Pages rendered inside it must not add their own `<main>` or sign-out control.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user, ability, logout, supportSession, supportSessionRemainingMs, endSupportSession } = useAuth();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useBodyScrollLock(drawerOpen);

  // Move focus into the drawer on open and hand it back to the hamburger on
  // close — otherwise focus is stranded on a button that no longer exists.
  useEffect(() => {
    if (drawerOpen) {
      wasOpen.current = true;
      drawerRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    } else if (wasOpen.current) {
      wasOpen.current = false;
      triggerRef.current?.focus();
    }
  }, [drawerOpen]);

  // Escape dismisses, and Tab cycles inside the dialog rather than escaping into
  // the page behind the scrim.
  useEffect(() => {
    if (!drawerOpen) return;

    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setDrawerOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;

      const focusable = Array.from(drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) return;

      const active = document.activeElement;

      if (event.shiftKey && (active === first || !drawerRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  // Growing past `lg` reveals the permanent sidebar, so a drawer left open would
  // otherwise keep the scroll lock and focus trap alive against nothing.
  useEffect(() => {
    if (!drawerOpen) return;
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setDrawerOpen(false);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [drawerOpen]);

  return (
    /*
     * One provider for the whole shell, so the sidebar badge, the Enquiries
     * page and the alert toasts all read ONE poll rather than three.
     */
    <EnquirySummaryProvider>
    <div className="min-h-svh bg-canvas text-fg">
      <a
        href="#main-content"
        className="sr-only rounded-md bg-accent-brand px-3 py-2 text-sm font-medium text-fg-on-accent focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50"
      >
        Skip to content
      </a>

      {/* The rail is app chrome, so it shares the top bar's rung — see the
          ladder in `src/index.css`. */}
      <div
        className={cx(
          'fixed inset-y-0 left-0 z-30 hidden border-r border-border transition-[width] duration-150 ease-out motion-reduce:transition-none lg:block',
          /*
           * 🔴 `w-72` (288px), NOT `w-64` (256px) — widened 2026-09-02 on the
           * owner's instruction, because `University Partnerships Dashboard`
           * did not fit: 214px of text against 193px of room, rendering as
           * `University Partnerships Dash…`.
           *
           * ⚠️ THE ALTERNATIVE WAS TO SHORTEN THE OWNER'S WORDING, and it was
           * put to them as such rather than taken quietly. The row was the only
           * one of the seven renamed that clipped; the other six fit at 256.
           *
           * ⚠️ THE RAIL AND THE PADDING BELOW ARE ONE MEASUREMENT IN TWO
           * PLACES. Tailwind cannot share a value between a width and a
           * padding here, so `w-72` must move with `lg:pl-72` — and the failure
           * of forgetting one is not an error but a 32px strip of dead space,
           * or a rail painted over the page, at one breakpoint.
           */
          collapsed ? 'w-16' : 'w-72',
        )}
      >
        <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
      </div>

      <div
        className={cx(
          'flex min-h-svh flex-col transition-[padding] duration-150 ease-out motion-reduce:transition-none',
          // Moves with the rail's width above. See the note there.
          collapsed ? 'lg:pl-16' : 'lg:pl-72',
        )}
      >
        {/*
         * `z-30` is the app-chrome rung of the ladder in `src/index.css`, and
         * it is the one number here that has to be right.
         *
         * This bar is `sticky` with a `z-index`, so it is a stacking context:
         * everything inside it — the account menu, the reminders bell — is
         * painted as part of THIS element, at THIS number, however high the
         * popover's own `z-30` is. At `z-20` it tied with `Table`'s pinned
         * header cell (`sticky right-0 z-20`), and an equal z-index is settled
         * by document order, so the table — always further down the page than
         * the bar — painted its pinned ACTIONS column straight over an open
         * account menu. Raising the MENU could never have fixed that; only
         * moving the bar off page content's top rung does.
         *
         * It stays below the drawer scrim (40), the drawer, `Modal` and the
         * toasts (50), and the portalled popovers (60), which is what lets a
         * dialog cover the chrome and a `Combobox` open over a dialog.
         *
         * The support-session banner is chrome too, so it shares this rung by
         * sitting INSIDE this one sticky element rather than becoming a second
         * one. Two `sticky top-0` siblings at the same rung would pin to the
         * same line and overlap — and which of them won would be settled by
         * document order, silently. As one block they stick together, and the
         * banner's height is never a number the bar has to know.
         */}
        <div className="sticky top-0 z-30">
          {/*
           * Above the support banner, and the order is a judgement rather than
           * an accident: this one answers "which application am I in", the
           * other "whose account am I in", and the first is the wider mistake.
           * Somebody acting as a colleague on the apex build is doing something
           * ordinary; the same person on a preview is one save away from
           * pushing a real document to Xero from unreviewed code.
           *
           * Both sit INSIDE this single sticky element for the reason the
           * comment above gives — two `sticky top-0` siblings on one rung pin
           * to the same line and overlap, with document order silently
           * deciding the winner. Adding a third banner means adding it here,
           * never beside this block.
           *
           * Renders null on the apex build, so it is mounted unconditionally
           * and needs no guard at the call site.
           */}
          <PreviewBanner />

          {supportSession && (
            <SupportSessionBanner
              key={supportSession.expiresAt}
              session={supportSession}
              remainingMs={supportSessionRemainingMs}
              onEnd={endSupportSession}
            />
          )}

          <header className="border-b border-border bg-surface">
            {/*
             * 🔴 THE BAR MUST FIT 320px FOR EVERY ROLE. It ran to x=374 for a
             * super admin and was clipped by `body`'s horizontal overflow — a
             * defect the overflow probe reports as clean. Below `sm` the gaps
             * and gutter tighten, the lockup is dropped (the drawer carries
             * the wordmark), the account chevron hides and the build pill goes
             * compact, which together bring the worst case (super admin, bell
             * with an overdue count) inside the viewport.
             */}
            <div className="flex h-14 items-center gap-1 px-3 sm:gap-3 sm:px-6">
              <Button
                ref={triggerRef}
                variant="ghost"
                iconOnly
                onClick={() => setDrawerOpen(true)}
                aria-label="Open navigation"
                aria-expanded={drawerOpen}
                leadingIcon={<MenuIcon />}
                className="lg:hidden"
              />

              {/* The wordmark lives in the sidebar; below `lg` that is off-canvas. */}
              {/* Opens the permission set's default dashboard, as the sidebar's does. */}
              {/*
               * `hidden sm:flex lg:hidden`: dropped below `sm`, where it
               * rendered as a 0×28 focus stop (its text span is hidden and the
               * `min-w-0` link collapsed around the mark) and where the bar has
               * no room for it. `min-w-7` keeps the mark's width from `sm` up,
               * so the link can never collapse to a zero-width ring again.
               */}
              <Link
                to={resolveLandingPathWithSet(null, ability.landingPath, ability)}
                aria-label="SEED EMERGE — go to your default dashboard"
                className="hidden min-w-7 items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-focus-ring sm:flex lg:hidden"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-md border border-border">
                  <EmergeMark className="h-full w-full p-0.5" />
                </span>
                <span className="hidden truncate text-sm font-semibold tracking-[-0.01em] text-fg sm:block">
                  SEED EMERGE
                </span>
              </Link>

              <GlobalSearch />

              <div className="ml-auto flex min-w-0 shrink-0 items-center gap-0.5 sm:gap-1">
                {/*
                 * Mounted in the shell rather than on /tasks, so a reminder reaches
                 * someone wherever they are in the app. Renders nothing when signed
                 * out.
                 */}
                <TaskReminders />
                {/* New-enquiry toasts, reading the shell's `EnquirySummaryProvider`. */}
                <EnquiryAlerts />
                {/*
                 * 🔴 **SUPER ADMIN ONLY, AND THE GATE IS HERE RATHER THAN IN THE
                 * COMPONENT.** `BuildSwitcher` renders unconditionally when
                 * mounted; this line is the only thing that decides who sees
                 * it, so it is the one place to look when answering "who can
                 * see the build pill". A gate inside the component would put
                 * that answer somewhere a reader of the shell cannot see.
                 *
                 * ⚠️ **`isSuperAdmin`, NOT `isAdminTier`.** `super_admin` is a
                 * strict superset of `admin`, so the admin-tier predicate right
                 * beside it in `@/lib/roles` passes for BOTH — using it would
                 * silently show the switcher to the two `admin` accounts as
                 * well. Owner's instruction, 8 September 2026: super admin
                 * alone.
                 *
                 * ⚠️ **This hides a MENU; it is not a security boundary and
                 * nothing here pretends otherwise.** The preview builds it
                 * lists are served by nginx to anyone holding the URL. What
                 * makes that acceptable is that a preview is still the ordinary
                 * app — every request it makes is authenticated and every guard
                 * the API has still refuses. See the note on the predicate
                 * itself in `@/lib/roles`.
                 *
                 * The component reads ONE static file, `/environments.json`,
                 * and calls no API. It cannot read or write a record, and it
                 * cannot see anything about another user.
                 */}
                {/*
                 * Hidden below `sm`: at 320 the pill's 78px left the search
                 * wrapper 18px wide, so its 44px button spilled left over the
                 * "Open navigation" button and took every tap meant for it.
                 */}
                {isSuperAdmin(user) && (
                  <div className="hidden sm:contents">
                    <BuildSwitcher />
                  </div>
                )}
                {/*
                 * The guide sits between the bell and the account menu, on the
                 * owner's instruction. Mounted in the shell so it is reachable
                 * from every signed-in page, and it reads the current route to
                 * decide which section to open on.
                 *
                 * ⚠️ A guide is NOT the helper text the house rule prohibits.
                 * That rule is about prose attached to a control; this is a
                 * destination somebody opens on purpose. The reasoning is
                 * written out in full at the top of `@/lib/guide-content`, so
                 * the next helper-text sweep meets it before deleting the file.
                 */}
                <GuideDrawer />
                {user && (
                  <UserMenu
                    user={{ name: user.name, email: user.email, role: ROLE_LABELS[user.role] }}
                    onSignOut={logout}
                  />
                )}
              </div>
            </div>
          </header>
        </div>

        {/*
         * 🔴 COLLAPSING THE RAIL GIVES THE PAGE THE WIDTH — owner's request,
         * 2026-09-02. Before this, `max-w-7xl` was unconditional, so collapsing
         * the sidebar bought 208px of MARGIN and not one pixel of content. The
         * control looked like it did something and, for the widest pages in the
         * app, did nothing at all.
         *
         * ⚠️ THE CAP IS RAISED, NOT REMOVED. Prose at full monitor width is
         * unreadable, and most of this app is forms and detail pages rather
         * than the tracker. `max-w-\[96rem\]` is 1536px against 7xl's 1280 —
         * so a collapsed rail is worth 256px of real content, which is roughly
         * two of the tracker's columns.
         *
         * ⚠️ IT IS A `max-width` SWAP AND MUST NOT BECOME A TRANSFORM, AN
         * OPACITY OR A `z-index`. The layer ladder in `src/index.css` records
         * that the tracker's full-screen sheet panel clears the `z-30` top bar
         * only because nothing between this `<main>` and that panel opens a
         * stacking context. Any of those three properties here would make the
         * panel paint UNDER the bar, with the right z-index and no error.
         *
         * The transition is on `max-width` alone for the same reason: animating
         * `transform` would create a stacking context for the duration.
         */}
        <main
          id="main-content"
          tabIndex={-1}
          className={cx(
            'mx-auto w-full flex-1 px-4 py-6 transition-[max-width] duration-150 ease-out motion-reduce:transition-none sm:px-6',
            collapsed ? 'max-w-[96rem]' : 'max-w-7xl',
          )}
        >
          {children}
        </main>
      </div>

      {drawerOpen && (
        <div className="lg:hidden">
          <div
            aria-hidden="true"
            onClick={closeDrawer}
            className="fixed inset-0 z-40 bg-scrim backdrop-blur-[2px]"
          />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            className="fixed inset-y-0 left-0 z-50 w-[min(17.5rem,85vw)] border-r border-border shadow-lg"
          >
            <Sidebar collapsed={false} onNavigate={closeDrawer} onClose={closeDrawer} />
          </div>
        </div>
      )}

      {/*
       * The window frame — the second half of the support-session signal, and
       * the half that survives a screenshot.
       *
       * ⚠️ `z-70` is a FIFTH rung on the ladder in `src/index.css`, above the
       * portalled popovers at 60, and it is the only thing in the app up there.
       * It has to be: the frame's claim is about the whole WINDOW, so the one
       * moment it must not be dimmed or covered is the moment somebody is
       * editing another person's record inside a `Modal` — the dialog sits at
       * 50 behind a scrim that would wash a frame on the chrome rung, and that
       * is precisely when the mistake this feature exists to prevent gets made.
       * Tying at 60 was the other option and is what the ladder forbids: equal
       * numbers are settled by document order, and a popover portalled into
       * `document.body` always lands after this element.
       *
       * It takes no layout and steals nothing. `fixed inset-0` sizes it to the
       * viewport exactly, so it is out of flow and adds no scrollable overflow;
       * `border-4` paints INSIDE that box under Tailwind's `border-box`
       * preflight, so no page shifts by 4px and nothing gains horizontal
       * scroll — which a `border` on a wrapper element would have done to every
       * page at once. `pointer-events-none` keeps it from swallowing a click on
       * anything it paints over, and `aria-hidden` keeps it out of the
       * accessibility tree: it is decoration, and the banner above carries the
       * same fact as real text.
       */}
      {supportSession && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-[70] border-4 border-warning"
        />
      )}
    </div>
    </EnquirySummaryProvider>
  );
}
